import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useSnackbar } from 'notistack';
import { Helmet } from 'react-helmet';
import { useForm } from 'react-hook-form';
import { useLocation, useParams } from 'react-router-dom';
import dayjs from '~/timeInit';

import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SaveIcon from '@mui/icons-material/Save';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Container,
  DialogActions,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import type { GridColDef, GridEventListener, GridRenderCellParams, GridRowModesModel } from '@mui/x-data-grid';
import { DataGrid, GridRowModes } from '@mui/x-data-grid';

import {
  type ChannelMarkupConditionApplyType,
  type ChannelMarkupConditionType,
  channelMarkupConditionConfig,
  currencyOptions,
  siteLocationConfig,
  subscriberTierOptions,
  userRoleOptions,
} from '@luxuryescapes/contract-svc-promo/channelMarkup';
import type { components } from '@luxuryescapes/contract-svc-promo/generated';

import GlobalScopedMultiSelect from '~/components/Common/Elements/GlobalScopedMultiSelect';
import PageSubheader from '~/components/Common/Elements/PageSubheader';
import DebugModal from '~/components/DebugModal/DebugModal';
import usePromoMeta from '~/components/Promo/hooks/usePromoMeta';
import VoucherConditionValueEditCell from '~/components/Vouchers/VoucherConditionValueEditCell';

import { toPromoBrand } from '~/consts/promoBrand';
import { ROLE_CHANNEL_MARKUP_ADMIN_USER } from '~/consts/roles';

import { useChannelMarkupV2 } from '~/hooks/apis/promo/useChannelMarkupV2';
import { useCheckChannelMarkupV2 } from '~/hooks/apis/promo/useCheckChannelMarkupV2';
import { useGetChannelMarkupV2ById } from '~/hooks/apis/promo/useGetChannelMarkupV2ById';
import useAppNavigation from '~/hooks/useAppNavigation';
import useCurrentTenant from '~/hooks/useCurrentTenant';
import useCurrentUser from '~/hooks/useCurrentUser';
import useDebounced from '~/hooks/useDebounced';

type ChannelMarkupV2Internal = import('@luxuryescapes/contract-svc-promo/channelMarkup').ChannelMarkupV2Internal;
type PromoConditionTypeConfig = (typeof channelMarkupConditionConfig)[number];

type ChannelMarkupHydrationCondition = {
  conditionId?: string;
  conditionType: string;
  conditionApplyType: string;
  conditionValue: string;
  specificMarkupIds?: Array<string> | null;
};

type ChannelMarkupLocationState = {
  channelMarkup?: ChannelMarkupV2Internal;
};

/**
 * (moving to contract type too)
 */
type FormCondition = {
  id: string;
  condition_type: ChannelMarkupConditionType;
  condition_apply_type: string;
  condition_value: string;
  specificMarkupIds: Array<string>;
};

type Markup = {
  id: string;
  markup_type: 'fixed' | 'percentage';
  markup_value: string;
  lux_plus_markup_value: string;
  channelMarkupCTAText: string;
  currency: components['schemas']['Promo Currencies'] | '';
};

type FormValues = {
  conditions: Array<FormCondition>;
  markups: Array<Markup>;
  expiresAt: string;
};

type TesterValues = {
  context: {
    region: string;
    currency: string;
    siteLocation: string;
    subscriberTier: string;
    roles: Array<string>;
    offer: {
      offerId: string;
      loyaltyOfferType: string;
      total: string;
      luxPlusPrice: string;
    };
  };
};

type TesterEvaluationValues = {
  context: import('@luxuryescapes/contract-svc-promo/channelMarkup').ChannelMarkupEvaluationContext;
};

function isChannelMarkupLocationState(value: unknown): value is ChannelMarkupLocationState {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  return 'channelMarkup' in value;
}

type RowSaveState = 'saved';

type ChannelMarkupHydrationSource = {
  channelMarkupId?: string;
  expiresAt?: string | null;
  expires_at?: string | null;
  rules?: {
    conditions?: Array<ChannelMarkupHydrationCondition>;
    markups?: Array<{
      markupId?: string;
      markupType: Markup['markup_type'];
      markupValue?: number | null;
      luxPlusMarkupValue?: number | null;
      channelMarkupCTAText?: string | null;
      currency?: Markup['currency'];
    }>;
  };
};

const defaultMarkup = (): Markup => ({
  id: crypto.randomUUID(),
  markup_type: 'percentage',
  markup_value: '',
  lux_plus_markup_value: '',
  channelMarkupCTAText: '',
  currency: 'AUD',
});

const isChannelMarkupConditionType = (value: unknown): value is ChannelMarkupConditionType =>
  typeof value === 'string' && channelMarkupConditionConfig.some((config) => config.key === value);

const isChannelMarkupConditionApplyType = (value: unknown): value is ChannelMarkupConditionApplyType =>
  typeof value === 'string' &&
  channelMarkupConditionConfig.some((config) =>
    config.conditionApplyTypes?.some((conditionApplyType) => conditionApplyType === value),
  );

const getDefaultConditionConfig = () => channelMarkupConditionConfig[0] ?? null;

const toNumberOrUndefined = (value: string): number | undefined => {
  if (value.trim() === '') {
    return undefined;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const buildChannelMarkupRulesPayload = (conditions: Array<FormCondition>, markups: Array<Markup>) => ({
  conditions: conditions.map((condition) => {
    const conditionConfig = isChannelMarkupConditionType(condition.condition_type)
      ? channelMarkupConditionConfig.find((config) => config.key === condition.condition_type)
      : getDefaultConditionConfig();
    const fallbackApplyType = conditionConfig?.conditionApplyTypes?.[0] ?? 'MUST-INCLUDE';
    const conditionType = conditionConfig?.key ?? condition.condition_type;
    const conditionApplyType = isChannelMarkupConditionApplyType(condition.condition_apply_type)
      ? condition.condition_apply_type
      : fallbackApplyType;

    return {
      conditionId: condition.id,
      specificMarkupIds: condition.specificMarkupIds.length > 0 ? condition.specificMarkupIds : null,
      conditionType,
      conditionApplyType,
      conditionValue: condition.condition_value,
    };
  }),
  markups: markups.map((markup) => ({
    markupId: markup.id,
    markupType: markup.markup_type,
    markupValue: toNumberOrUndefined(markup.markup_value),
    luxPlusMarkupValue: toNumberOrUndefined(markup.lux_plus_markup_value),
    channelMarkupCTAText: markup.channelMarkupCTAText || null,
    ...(markup.currency ? { currency: markup.currency } : {}),
  })),
});

const buildCreateChannelMarkupPayload = (
  tenantBrand: components['schemas']['Promo Brands'],
  conditions: Array<FormCondition>,
  markups: Array<Markup>,
  expiresAt: string,
) => ({
  brand: tenantBrand,
  ...(expiresAt.trim() ? { expiresAt: dayjs(expiresAt).toISOString() } : {}),
  rules: buildChannelMarkupRulesPayload(conditions, markups),
});

const buildUpdateChannelMarkupPayload = (
  channelMarkupId: string,
  tenantBrand: components['schemas']['Promo Brands'],
  conditions: Array<FormCondition>,
  markups: Array<Markup>,
  expiresAt: string,
) => ({
  channelMarkupId,
  brand: tenantBrand,
  ...(expiresAt.trim() ? { expiresAt: dayjs(expiresAt).toISOString() } : {}),
  rules: buildChannelMarkupRulesPayload(conditions, markups),
});

const renderMarkupValue = (markupType: Markup['markup_type'], value: string) => {
  const numericValue = Number(value);

  if (value.trim() === '' || (Number.isFinite(numericValue) && numericValue === 0)) {
    return '--';
  }

  return markupType === 'fixed' ? `$${value}` : `${value}%`;
};

const getConditionConfig = (conditionTypeConfigMap: Map<string, PromoConditionTypeConfig>, conditionType: string) =>
  conditionTypeConfigMap.get(conditionType);

const getConditionTypeLabel = (conditionTypeOptions: Array<{ label: string; value: string }>, conditionType: string) =>
  conditionTypeOptions.find((option) => option.value === conditionType)?.label ?? conditionType;

const conditionColumns = (
  markups: Array<Markup>,
  conditionTypeOptions: Array<{ label: string; value: string }>,
  conditionTypeConfigMap: Map<string, PromoConditionTypeConfig>,
  onRemove: (id: string) => void,
  onUpdate: (id: string, patch: Partial<FormCondition>) => void,
  showDetails: boolean,
): Array<GridColDef<FormCondition>> => [
  {
    field: 'condition_type',
    headerName: 'Condition Type',
    flex: 1,
    renderCell: (params) => {
      const conditionType = params.row.condition_type;
      const conditionConfig = getConditionConfig(conditionTypeConfigMap, conditionType);

      return showDetails && conditionConfig?.longDescription ? (
        <Stack spacing={0.25}>
          <Typography sx={{ fontWeight: 600 }}>{getConditionTypeLabel(conditionTypeOptions, conditionType)}</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
            {conditionConfig.longDescription}
          </Typography>
        </Stack>
      ) : (
        <Typography sx={{ fontWeight: 600 }}>{getConditionTypeLabel(conditionTypeOptions, conditionType)}</Typography>
      );
    },
    renderEditCell: (params) => (
      <Stack spacing={0.5}>
        <TextField
          select
          autoFocus
          fullWidth
          value={params.value ?? ''}
          onChange={(event) => {
            const conditionType = event.target.value;
            if (!isChannelMarkupConditionType(conditionType)) {
              return;
            }
            const nextConfig = getConditionConfig(conditionTypeConfigMap, conditionType);
            const nextApplyType =
              nextConfig?.conditionApplyTypes?.[0] ?? (params.row.condition_apply_type || 'MUST-INCLUDE');
            params.api.setEditCellValue({ id: params.id, field: params.field, value: conditionType });
            onUpdate(String(params.id), {
              condition_type: conditionType,
              condition_apply_type: nextApplyType,
              condition_value: '',
            });
            params.api.setEditCellValue({
              id: params.id,
              field: 'condition_apply_type',
              value: nextApplyType,
            });
            params.api.setEditCellValue({ id: params.id, field: 'condition_value', value: '' });
          }}
          helperText={showDetails ? 'Select what kind of condition this rule uses.' : undefined}
        >
          {conditionTypeOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
        {showDetails && getConditionConfig(conditionTypeConfigMap, params.row.condition_type)?.longDescription ? (
          <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
            {getConditionConfig(conditionTypeConfigMap, params.row.condition_type)?.longDescription}
          </Typography>
        ) : null}
      </Stack>
    ),
    editable: true,
    sortable: false,
    filterable: false,
  },
  {
    field: 'condition_apply_type',
    headerName: 'Apply Type',
    flex: 1,
    renderEditCell: (params) => {
      const conditionType = params.row.condition_type;
      const conditionConfig = getConditionConfig(conditionTypeConfigMap, conditionType);
      const conditionApplyTypes = conditionConfig?.conditionApplyTypes ?? [];

      if (conditionApplyTypes.length === 0) {
        return (
          <Typography variant="body2" color="text.secondary">
            --
          </Typography>
        );
      }

      return (
        <TextField
          select
          autoFocus
          fullWidth
          value={params.value ?? ''}
          onChange={(event) => {
            const conditionApplyType = event.target.value;
            params.api.setEditCellValue({ id: params.id, field: params.field, value: conditionApplyType });
            onUpdate(String(params.id), {
              condition_apply_type: conditionApplyType,
            });
          }}
          helperText={showDetails ? `Use one of the apply types configured for ${conditionType}.` : undefined}
        >
          {conditionApplyTypes.map((option) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </TextField>
      );
    },
    editable: true,
    sortable: false,
    filterable: false,
  },
  {
    field: 'condition_value',
    headerName: 'Condition Value',
    flex: 1.2,
    renderEditCell: (params) => (
      <VoucherConditionValueEditCell
        {...params}
        isDatetime={false}
        suggestedConditionValues={[
          ...(getConditionConfig(conditionTypeConfigMap, params.row.condition_type)?.suggestedConditionValues ?? []),
        ]}
      />
    ),
    preProcessEditCellProps: (params) => ({
      ...params.props,
      error: String(params.props.value ?? '').trim() === '',
    }),
    editable: true,
    sortable: false,
    filterable: false,
  },
  {
    field: 'specificMarkupIds',
    headerName: 'Specific Markups',
    flex: 1.3,
    renderCell: (params: GridRenderCellParams<FormCondition, Array<string>>) => {
      const selectedMarkupIds = params.row.specificMarkupIds;

      if (!selectedMarkupIds.length) {
        return <Typography sx={{ fontWeight: 600 }}>Applies to all markups</Typography>;
      }

      return selectedMarkupIds.join(', ');
    },
    renderEditCell: (params) => (
      <GlobalScopedMultiSelect
        value={params.value ?? []}
        globalLabel="Applies to all markups"
        hideWhenGlobal
        options={markups.map((markup) => ({
          value: markup.id,
          label: (
            <>
              {markup.markup_type} {markup.markup_value || markup.lux_plus_markup_value ? '' : '(empty)'}
            </>
          ),
        }))}
        onChange={(specificMarkupIds) => {
          params.api.setEditCellValue({ id: params.id, field: params.field, value: specificMarkupIds });
          onUpdate(String(params.id), { specificMarkupIds });
        }}
      />
    ),
    editable: true,
    sortable: false,
    filterable: false,
    width: 260,
  },
  {
    field: 'debug',
    headerName: 'Debug',
    flex: 1.4,
    renderCell: (params) =>
      showDetails ? (
        <Typography
          component="pre"
          variant="caption"
          sx={{
            m: 0,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            fontFamily: 'monospace',
          }}
        >
          {JSON.stringify(params.row, null, 2)}
        </Typography>
      ) : null,
    editable: false,
    sortable: false,
    filterable: false,
  },
];

const markupColumns = (showDetails: boolean): Array<GridColDef<Markup>> => [
  {
    field: 'markup_type',
    headerName: 'Markup Type',
    flex: 1,
    renderEditCell: (params) => (
      <TextField
        select
        autoFocus
        fullWidth
        value={params.row.markup_type}
        onChange={(event) =>
          params.api.setEditCellValue({ id: params.id, field: 'markup_type', value: event.target.value })
        }
        helperText={
          showDetails ? 'Choose percentage for a relative adjustment or fixed for an absolute amount.' : undefined
        }
      >
        <MenuItem value="percentage">percentage</MenuItem>
        <MenuItem value="fixed">fixed</MenuItem>
      </TextField>
    ),
    editable: true,
    sortable: false,
    filterable: false,
  },
  {
    field: 'markup_value',
    headerName: 'Normal Price Markup',
    flex: 1,
    renderCell: (params) => renderMarkupValue(params.row.markup_type, params.row.markup_value),
    renderEditCell: (params) => (
      <TextField
        autoFocus
        fullWidth
        value={params.row.markup_value}
        onChange={(event) =>
          params.api.setEditCellValue({ id: params.id, field: 'markup_value', value: event.target.value })
        }
        helperText={showDetails ? 'Enter the markup for normal prices.' : undefined}
      />
    ),
    editable: true,
    sortable: false,
    filterable: false,
  },
  {
    field: 'lux_plus_markup_value',
    headerName: 'Lux Plus Markup',
    flex: 1,
    renderCell: (params) => renderMarkupValue(params.row.markup_type, params.row.lux_plus_markup_value),
    renderEditCell: (params) => (
      <TextField
        autoFocus
        fullWidth
        value={params.row.lux_plus_markup_value}
        onChange={(event) =>
          params.api.setEditCellValue({ id: params.id, field: 'lux_plus_markup_value', value: event.target.value })
        }
        helperText={showDetails ? 'Enter the Lux Plus-specific markup.' : undefined}
      />
    ),
    editable: true,
    sortable: false,
    filterable: false,
  },
  {
    field: 'channelMarkupCTAText',
    headerName: 'CTA Text',
    flex: 1,
    renderEditCell: (params) => (
      <TextField
        autoFocus
        fullWidth
        value={params.row.channelMarkupCTAText}
        onChange={(event) =>
          params.api.setEditCellValue({ id: params.id, field: 'channelMarkupCTAText', value: event.target.value })
        }
        helperText={showDetails ? 'Optional CTA text shown for this markup.' : undefined}
      />
    ),
    editable: true,
    sortable: false,
    filterable: false,
  },
  {
    field: 'currency',
    headerName: 'Currency',
    flex: 0.8,
    renderCell: (params) => <Typography sx={{ fontWeight: 600 }}>{params.row.currency || 'AUD'}</Typography>,
    renderEditCell: (params) => (
      <FormControl fullWidth>
        <InputLabel id={`currency-${params.id}-label`}>Currency</InputLabel>
        <Select
          labelId={`currency-${params.id}-label`}
          label="Currency"
          value={params.row.currency || 'AUD'}
          onChange={(event: SelectChangeEvent<string>) => {
            params.api.setEditCellValue({ id: params.id, field: params.field, value: event.target.value });
          }}
        >
          {currencyOptions.map((option) => (
            <MenuItem key={option.key} value={option.key}>
              {option.displayName}
            </MenuItem>
          ))}
        </Select>
        {showDetails && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Select the currency scope for this markup.
          </Typography>
        )}
      </FormControl>
    ),
    editable: true,
    sortable: false,
    filterable: false,
  },
  {
    field: 'debug',
    headerName: 'Debug',
    flex: 1.4,
    renderCell: (params) =>
      showDetails ? (
        <Typography
          component="pre"
          variant="caption"
          sx={{
            m: 0,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            fontFamily: 'monospace',
          }}
        >
          {JSON.stringify(params.row, null, 2)}
        </Typography>
      ) : null,
    editable: false,
    sortable: false,
    filterable: false,
  },
];

export default function ChannelMarkupCreateV2Page() {
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useCurrentUser();
  const tenant = useCurrentTenant();
  const { promoMeta } = usePromoMeta();
  const { evaluateChannelMarkup, applyChannelMarkup } = useChannelMarkupV2();
  const { push, goBack } = useAppNavigation();
  const { channelMarkupId } = useParams<{ channelMarkupId?: string }>();
  const location = useLocation();
  const initialChannelMarkup = isChannelMarkupLocationState(location.state) ? location.state.channelMarkup : undefined;
  const isEditMode = Boolean(channelMarkupId);
  const {
    data: serverChannelMarkup,
    isError: isChannelMarkupFetchError,
    isLoading: isChannelMarkupLoading,
  } = useGetChannelMarkupV2ById({ channelMarkupId, enabled: isEditMode });
  const draftChannelMarkupIdRef = useRef<string>(crypto.randomUUID());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [saveWarnings, setSaveWarnings] = useState<Array<string>>([]);
  const [testerCheckVersion, setTesterCheckVersion] = useState(0);
  const [conditionRowModesModel, setConditionRowModesModel] = useState<GridRowModesModel>({});
  const [markupRowModesModel, setMarkupRowModesModel] = useState<GridRowModesModel>({});
  const [conditionRowSaveState, setConditionRowSaveState] = useState<Record<string, RowSaveState>>({});
  const [markupRowSaveState, setMarkupRowSaveState] = useState<Record<string, RowSaveState>>({});
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const {
    register: registerTester,
    watch: watchTester,
    setValue: setTesterValue,
  } = useForm<TesterValues>({
    defaultValues: {
      context: {
        region: 'AU',
        currency: 'AUD',
        siteLocation: 'offer',
        subscriberTier: '',
        roles: [],
        offer: {
          offerId: '',
          loyaltyOfferType: 'HOTEL_FLASH',
          total: '',
          luxPlusPrice: '',
        },
      },
    },
  });

  const { handleSubmit, reset } = useForm<FormValues>({
    defaultValues: {
      conditions: [],
      markups: [],
      expiresAt: '',
    },
  });
  const canEditRows =
    Object.values(conditionRowModesModel).every((rowMode) => rowMode.mode !== GridRowModes.Edit) &&
    Object.values(markupRowModesModel).every((rowMode) => rowMode.mode !== GridRowModes.Edit);
  const conditionTypeOptions = useMemo(
    () =>
      channelMarkupConditionConfig.map((config) => ({
        label: config.name,
        value: config.key,
      })),
    [],
  );
  const conditionTypeConfigMap = useMemo(
    () => new Map(channelMarkupConditionConfig.map((config) => [config.key, config])),
    [],
  );
  const createDefaultCondition = useCallback((): FormCondition | null => {
    const defaultConfig = channelMarkupConditionConfig[0];

    if (!defaultConfig) {
      return null;
    }

    return {
      id: crypto.randomUUID(),
      condition_type: defaultConfig.key,
      condition_apply_type: defaultConfig.conditionApplyTypes?.[0] ?? 'MUST-INCLUDE',
      condition_value: '',
      specificMarkupIds: [],
    };
  }, []);
  const [conditions, setConditions] = useState<Array<FormCondition>>([]);
  const [markups, setMarkups] = useState<Array<Markup>>([defaultMarkup()]);
  const [expiresAt, setExpiresAt] = useState<string>('');
  const tenantBrand = useMemo(() => toPromoBrand(tenant.tenant.brand) ?? 'luxuryescapes', [tenant.tenant.brand]);
  const hasWarnedAboutTenantBrandRef = useRef(false);

  useEffect(() => {
    if (hasWarnedAboutTenantBrandRef.current) {
      return;
    }

    if (!toPromoBrand(tenant.tenant.brand)) {
      hasWarnedAboutTenantBrandRef.current = true;
      enqueueSnackbar(
        `Tenant brand "${tenant.tenant.brand}" is not configured for the channel markup editor. Defaulting to luxuryescapes.`,
        { variant: 'warning' },
      );
    }
  }, [enqueueSnackbar, tenant.tenant.brand]);

  const createPayload = useMemo(
    () => buildCreateChannelMarkupPayload(tenantBrand, conditions, markups, expiresAt),
    [conditions, expiresAt, markups, tenantBrand],
  );

  const updatePayload = useMemo(
    () => buildUpdateChannelMarkupPayload(draftChannelMarkupIdRef.current, tenantBrand, conditions, markups, expiresAt),
    [conditions, expiresAt, markups, tenantBrand],
  );

  // Live server-side validation of the draft markup, driving canSave + warnings.
  const hasCheckInputs = conditions.length > 0 && markups.length > 0;
  const checkPayload = useMemo(
    () => ({ channelMarkup: isEditMode ? updatePayload : createPayload }),
    [createPayload, isEditMode, updatePayload],
  );
  // Debounce so rapid edits don't fire a check request per keystroke.
  const debouncedCheckPayload = useDebounced(checkPayload, 300);
  const {
    data: checkResult,
    isFetching: isChecking,
    error: checkError,
    refetch: refetchCheck,
  } = useCheckChannelMarkupV2({ payload: debouncedCheckPayload, enabled: hasCheckInputs });
  const canSave = hasCheckInputs && Boolean(checkResult?.canSave);
  // keepPreviousData would otherwise retain the last check's warnings after all rows are removed.
  const checkWarnings = useMemo(
    () => (hasCheckInputs ? (checkResult?.warnings ?? []) : []),
    [hasCheckInputs, checkResult?.warnings],
  );

  const conditionGridColumns = useMemo(
    () =>
      conditionColumns(
        markups,
        conditionTypeOptions,
        conditionTypeConfigMap,
        (id) => setConditions((current) => current.filter((row) => row.id !== id)),
        (id, patch) => setConditions((current) => current.map((row) => (row.id === id ? { ...row, ...patch } : row))),
        showDetails,
      ),
    [conditionTypeConfigMap, conditionTypeOptions, markups, showDetails],
  );

  const markupGridColumns = useMemo(() => markupColumns(showDetails), [showDetails]);
  const testerValues = watchTester();
  const selectedCurrencies = useMemo(
    () => [testerValues.context.currency.trim() || 'AUD'],
    [testerValues.context.currency],
  );
  const effectiveTesterContext = useMemo<TesterEvaluationValues['context']>(
    () => ({
      region: testerValues.context.region.trim() || 'AU',
      currency: testerValues.context.currency.trim() || 'AUD',
      siteLocation: testerValues.context.siteLocation.trim() || 'offer',
      subscriberTier: testerValues.context.subscriberTier.trim() || null,
      roles: testerValues.context.roles,
      offer: {
        version: 'cmOfferV1',
        total: Number(testerValues.context.offer.total.trim() || 0),
        luxPlusPrice:
          testerValues.context.offer.luxPlusPrice.trim() === ''
            ? null
            : Number(testerValues.context.offer.luxPlusPrice),
        offerId: testerValues.context.offer.offerId.trim() || 'test-offer',
        loyaltyOfferType: testerValues.context.offer.loyaltyOfferType.trim() || 'HOTEL_FLASH',
      },
    }),
    [testerValues],
  );
  const canEditMarkup = Boolean(user?.roles?.includes(ROLE_CHANNEL_MARKUP_ADMIN_USER));
  const debugState = useMemo(
    () => ({
      row: {
        isEditMode,
        isSubmitting,
        showDetails,
        hasUnsavedChanges,
        draftChannelMarkupId: draftChannelMarkupIdRef.current,
        selectedCurrencies,
        testerValues,
        conditionRowModesModel,
        markupRowModesModel,
        conditionRowSaveState,
        markupRowSaveState,
        conditions,
        markups,
        expiresAt,
        createPayload,
        updatePayload,
      },
      conditionConfig: {
        conditionTypeOptions,
        siteLocationOptions: siteLocationConfig,
        subscriberTierOptions,
        userRoleOptions,
        currencyOptions,
      },
      ...(saveWarnings.length > 0 || checkWarnings.length > 0
        ? {
            errorsWarnings: {
              errors: [],
              warnings: [...checkWarnings, ...saveWarnings],
            },
          }
        : {}),
    }),
    [
      conditionRowModesModel,
      conditionRowSaveState,
      conditions,
      createPayload,
      hasUnsavedChanges,
      isEditMode,
      conditionTypeOptions,
      isSubmitting,
      markupRowModesModel,
      markupRowSaveState,
      markups,
      selectedCurrencies,
      showDetails,
      testerValues,
      updatePayload,
      expiresAt,
      saveWarnings,
      checkWarnings,
    ],
  );
  const testerResults = useMemo(() => {
    if (testerCheckVersion === 0) {
      return [];
    }

    return selectedCurrencies.map((currency) =>
      evaluateChannelMarkup({
        channelMarkupId: draftChannelMarkupIdRef.current,
        conditions,
        markups,
        testerValues: {
          context: {
            ...effectiveTesterContext,
            currency,
          },
        },
      }),
    );
  }, [
    conditions,
    effectiveTesterContext,
    evaluateChannelMarkup,
    markups,
    selectedCurrencies,
    tenant.tenant.brand,
    testerCheckVersion,
  ]);
  const testerResult = useMemo(() => {
    if (testerResults.some((result) => result.status === 'matched')) {
      return 'matched';
    }
    if (testerResults.some((result) => result.status === 'pending')) {
      return 'pending';
    }
    return 'no-match';
  }, [testerResults]);

  useEffect(() => {
    if (conditionTypeOptions.length === 0 || conditions.length > 0) {
      return;
    }

    const defaultCondition = createDefaultCondition();
    if (defaultCondition) {
      setConditions([defaultCondition]);
    }
  }, [conditionTypeOptions.length, conditions.length, createDefaultCondition]);

  const markConditionSaved = useCallback((id: string) => {
    setConditionRowSaveState((current) => ({ ...current, [id]: 'saved' }));
    window.setTimeout(() => {
      setConditionRowSaveState((current) => {
        if (current[id] !== 'saved') {
          return current;
        }

        const next = { ...current };
        delete next[id];
        return next;
      });
    }, 1200);
  }, []);

  const markMarkupSaved = useCallback((id: string) => {
    setMarkupRowSaveState((current) => ({ ...current, [id]: 'saved' }));
    window.setTimeout(() => {
      setMarkupRowSaveState((current) => {
        if (current[id] !== 'saved') {
          return current;
        }

        const next = { ...current };
        delete next[id];
        return next;
      });
    }, 1200);
  }, []);

  const handleRowEditStop = useCallback<GridEventListener<'rowEditStop'>>((params, event) => {
    if (params.reason === 'rowFocusOut') {
      event.defaultMuiPrevented = true;
    }
  }, []);

  const conditionGridWithActions = useMemo<Array<GridColDef<FormCondition>>>(() => {
    return [
      ...conditionGridColumns,
      {
        field: 'actions',
        headerName: '',
        sortable: false,
        filterable: false,
        width: 120,
        renderCell: (params) => {
          const isEditing = conditionRowModesModel[params.id]?.mode === GridRowModes.Edit;
          return (
            <Stack direction="row" spacing={0.5}>
              {isEditing ? (
                <>
                  <IconButton
                    aria-label="Save condition"
                    size="small"
                    onClick={() => params.api.stopRowEditMode({ id: params.id })}
                  >
                    <SaveIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    aria-label="Cancel condition edit"
                    size="small"
                    onClick={() => params.api.stopRowEditMode({ id: params.id, ignoreModifications: true })}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    aria-label="Remove condition"
                    size="small"
                    onClick={() => {
                      params.api.stopRowEditMode({ id: params.id, ignoreModifications: true });
                      setConditions((current) => current.filter((row) => row.id !== params.id));
                      setConditionRowModesModel((current) => {
                        const next = { ...current };
                        delete next[params.id];
                        return next;
                      });
                      setHasUnsavedChanges(true);
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </>
              ) : (
                <Stack direction="row" spacing={0.5} alignItems="center">
                  {conditionRowSaveState[String(params.id)] === 'saved' && (
                    <Typography variant="caption" color="success.main" sx={{ fontWeight: 600 }}>
                      Saved
                    </Typography>
                  )}
                  <IconButton
                    aria-label="Edit condition"
                    size="small"
                    onClick={() =>
                      setConditionRowModesModel((current) => ({
                        ...current,
                        [params.id]: { mode: GridRowModes.Edit },
                      }))
                    }
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Stack>
              )}
            </Stack>
          );
        },
      },
    ];
  }, [conditionGridColumns, conditionRowModesModel, conditionRowSaveState]);

  const markupGridWithActions = useMemo<Array<GridColDef<Markup>>>(() => {
    return [
      ...markupGridColumns,
      {
        field: 'actions',
        headerName: 'Actions',
        sortable: false,
        filterable: false,
        width: 132,
        renderCell: (params) => {
          const isEditing = markupRowModesModel[params.id]?.mode === GridRowModes.Edit;
          return (
            <Stack direction="row" spacing={0.5}>
              {isEditing ? (
                <>
                  <IconButton
                    aria-label="Save markup"
                    size="small"
                    onClick={() => params.api.stopRowEditMode({ id: params.id })}
                  >
                    <SaveIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    aria-label="Cancel markup edit"
                    size="small"
                    onClick={() => params.api.stopRowEditMode({ id: params.id, ignoreModifications: true })}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    aria-label="Remove markup"
                    size="small"
                    onClick={() => {
                      setMarkups((current) => current.filter((row) => row.id !== params.id));
                      setHasUnsavedChanges(true);
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </>
              ) : (
                <Stack direction="row" spacing={0.5} alignItems="center">
                  {markupRowSaveState[String(params.id)] === 'saved' && (
                    <Typography variant="caption" color="success.main" sx={{ fontWeight: 600 }}>
                      Saved
                    </Typography>
                  )}
                  <IconButton
                    aria-label="Edit markup"
                    size="small"
                    onClick={() =>
                      setMarkupRowModesModel((current) => ({
                        ...current,
                        [params.id]: { mode: GridRowModes.Edit },
                      }))
                    }
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Stack>
              )}
            </Stack>
          );
        },
      },
    ];
  }, [markupGridColumns, markupRowModesModel, markupRowSaveState]);

  // Surface check failures to the user.
  useEffect(() => {
    if (checkError) {
      enqueueSnackbar(`Failed to validate channel markup: ${checkError}`, { variant: 'error' });
    }
  }, [checkError, enqueueSnackbar]);

  const handleRefreshCheck = useCallback(() => {
    void refetchCheck();
  }, [refetchCheck]);

  const hydrateFormFromChannelMarkup = useCallback(
    (channelMarkup: ChannelMarkupHydrationSource) => {
      const rawConditions = channelMarkup.rules?.conditions ?? [];
      const initialConditions: Array<FormCondition> = rawConditions.flatMap((condition) => {
        const conditionType = isChannelMarkupConditionType(condition.conditionType)
          ? condition.conditionType
          : channelMarkupConditionConfig[0]?.key;

        if (!conditionType) {
          return [];
        }

        return [
          {
            id: condition.conditionId ?? crypto.randomUUID(),
            condition_type: conditionType,
            condition_apply_type: condition.conditionApplyType,
            condition_value: condition.conditionValue,
            specificMarkupIds: condition.specificMarkupIds ?? [],
          },
        ];
      });
      const initialMarkups = channelMarkup.rules?.markups?.map((markup) => ({
        id: markup.markupId ?? crypto.randomUUID(),
        markup_type: markup.markupType,
        markup_value: markup.markupValue?.toString() ?? '',
        lux_plus_markup_value: markup.luxPlusMarkupValue?.toString() ?? '',
        channelMarkupCTAText: markup.channelMarkupCTAText ?? '',
        currency: markup.currency ?? 'AUD',
      })) ?? [defaultMarkup()];
      const rawExpiresAt = channelMarkup.expiresAt ?? channelMarkup.expires_at;
      const initialExpiresAt = rawExpiresAt
        ? dayjs(rawExpiresAt).isValid()
          ? dayjs(rawExpiresAt).format('YYYY-MM-DDTHH:mm')
          : ''
        : '';
      const unsupportedConditions = rawConditions.filter(
        (condition) =>
          !isChannelMarkupConditionType(condition.conditionType) ||
          !isChannelMarkupConditionApplyType(condition.conditionApplyType),
      );

      const nextChannelMarkupId = channelMarkup.channelMarkupId ?? channelMarkupId ?? draftChannelMarkupIdRef.current;
      draftChannelMarkupIdRef.current = String(nextChannelMarkupId);

      if (unsupportedConditions.length > 0) {
        enqueueSnackbar(
          `Loaded channel markup contains ${unsupportedConditions.length} unsupported condition rule${
            unsupportedConditions.length === 1 ? '' : 's'
          }. Those values were kept as-is so you can review or correct them before saving.`,
          { variant: 'warning' },
        );
      }

      const fallbackCondition = createDefaultCondition();
      setConditions(initialConditions.length > 0 ? initialConditions : fallbackCondition ? [fallbackCondition] : []);
      setMarkups(initialMarkups.length > 0 ? initialMarkups : [defaultMarkup()]);
      setExpiresAt(initialExpiresAt);
      setHasUnsavedChanges(false);
      setConditionRowSaveState({});
      setMarkupRowSaveState({});
    },
    [channelMarkupId, createDefaultCondition, enqueueSnackbar],
  );

  const handleCreate = handleSubmit(async () => {
    if (!canSave || isChecking || !canEditRows) {
      return;
    }

    setIsSubmitting(true);
    setSaveWarnings([]);
    try {
      const { channelMarkup, warnings } = await applyChannelMarkup({
        isEditMode,
        createPayload,
        updatePayload,
      });

      setSaveWarnings(warnings);
      warnings.forEach((warning) => enqueueSnackbar(warning, { variant: 'warning' }));

      reset();
      enqueueSnackbar(`Channel Markup v2 ${isEditMode ? 'updated' : 'created'} successfully`, {
        variant: 'success',
      });
      if (!isEditMode) {
        setConditions([createDefaultCondition()]);
        setMarkups([defaultMarkup()]);
        setExpiresAt('');
        setHasUnsavedChanges(false);
        setConditionRowSaveState({});
        setMarkupRowSaveState({});
        push('/marketing/channel-markup');
        return;
      }

      if (channelMarkup) {
        hydrateFormFromChannelMarkup(channelMarkup);
      }
    } catch (error) {
      enqueueSnackbar(`Channel Markup v2 ${isEditMode ? 'update' : 'creation'} failed: ${error}`, {
        variant: 'error',
      });
    } finally {
      setIsSubmitting(false);
    }
  });

  useEffect(() => {
    const channelMarkup = serverChannelMarkup ?? initialChannelMarkup;

    if (!channelMarkup || (isEditMode && isChannelMarkupLoading)) {
      return;
    }
    hydrateFormFromChannelMarkup(channelMarkup);
  }, [
    createDefaultCondition,
    hydrateFormFromChannelMarkup,
    initialChannelMarkup,
    isChannelMarkupLoading,
    isEditMode,
    serverChannelMarkup,
  ]);

  if (isEditMode && isChannelMarkupLoading && !initialChannelMarkup) {
    return (
      <Container maxWidth="xl">
        <Alert severity="info">Loading channel markup...</Alert>
      </Container>
    );
  }

  if (isEditMode && isChannelMarkupFetchError && !initialChannelMarkup && !serverChannelMarkup) {
    return (
      <Container maxWidth="xl">
        <Alert severity="error">Unable to load channel markup.</Alert>
      </Container>
    );
  }

  if (conditionTypeOptions.length === 0) {
    return (
      <Container maxWidth="xl">
        <Alert severity="error" variant="outlined">
          No channel markup condition types are available. Check the contract configuration.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      <Helmet>
        <title>Channel Markup v2 | {isEditMode ? 'Edit' : 'Create'}</title>
      </Helmet>

      <PageSubheader title={isEditMode ? 'Edit Channel Markup v2' : 'Create Channel Markup v2'}>
        <Button variant="text" onClick={() => goBack()}>
          Back to list
        </Button>
      </PageSubheader>

      <Box component="form" onSubmit={handleCreate} mt={2} data-testid="channel-markup-v2-form">
        <Stack spacing={3} maxWidth={1200}>
          {saveWarnings.length > 0 && (
            <Alert severity="warning" variant="outlined">
              <Stack spacing={0.5} component="ul" sx={{ m: 0, pl: 2 }}>
                {saveWarnings.map((warning, index) => (
                  <li key={`${warning}-${index}`}>
                    <Typography variant="body2">{warning}</Typography>
                  </li>
                ))}
              </Stack>
            </Alert>
          )}

          {!canSave && !isChecking && (
            <Alert severity="warning" variant="outlined">
              <Stack spacing={1}>
                <Stack spacing={0.5} component="ul" sx={{ m: 0, pl: 2 }}>
                  {checkWarnings.length > 0 ? (
                    checkWarnings.map((warning, index) => (
                      <li key={`${warning}-${index}`}>
                        <Typography variant="body2">{warning}</Typography>
                      </li>
                    ))
                  ) : (
                    <li>
                      <Typography variant="body2">
                        Channel markup cannot be saved yet. Refresh the check after making changes.
                      </Typography>
                    </li>
                  )}
                </Stack>
                <Box>
                  <Button variant="text" onClick={handleRefreshCheck} disabled={isChecking || isSubmitting}>
                    Refresh check
                  </Button>
                </Box>
              </Stack>
            </Alert>
          )}

          {showDetails && (
            <Alert severity="info" variant="outlined">
              <Stack spacing={0.5}>
                <Typography variant="subtitle2">Channel markup details</Typography>
                <Typography variant="body2">
                  Markups control the actual pricing adjustment. Conditions decide when a markup applies. Edit a row to
                  see field-level guidance.
                </Typography>
              </Stack>
            </Alert>
          )}

          <Stack spacing={1.5}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Stack spacing={0.5}>
                <Typography variant="h6">Markups</Typography>
                {showDetails && (
                  <Typography variant="body2" color="text.secondary">
                    Markups define how much the normal price or Lux Plus price is adjusted. They include the currency
                    and optional Call To Action text. This text supports replacement logic via templated
                    post-calculation fields
                  </Typography>
                )}
              </Stack>
            </Stack>
            <DataGrid
              autoHeight
              hideFooter
              disableRowSelectionOnClick
              getRowHeight={() => 'auto'}
              rows={markups}
              columns={markupGridWithActions}
              columnVisibilityModel={{ debug: false }}
              editMode="row"
              rowModesModel={markupRowModesModel}
              onRowModesModelChange={setMarkupRowModesModel}
              onRowEditStop={handleRowEditStop}
              processRowUpdate={(updatedRow) => {
                setMarkups((current) =>
                  current.map((row) => (row.id === updatedRow.id ? { ...row, ...updatedRow } : row)),
                );
                setHasUnsavedChanges(true);
                markMarkupSaved(updatedRow.id);
                return updatedRow;
              }}
              onProcessRowUpdateError={(error) => {
                enqueueSnackbar(`Failed to update markup row: ${error}`, { variant: 'error' });
              }}
              sx={{
                '& .MuiDataGrid-cell': { alignItems: 'flex-start', py: 1 },
                '& .MuiDataGrid-row': { maxHeight: 'none !important' },
              }}
            />
            <Button
              startIcon={<AddIcon />}
              variant="text"
              onClick={() => {
                const newMarkup = defaultMarkup();
                setMarkups((current) => current.concat(newMarkup));
                setMarkupRowModesModel((current) => ({
                  ...current,
                  [newMarkup.id]: { mode: GridRowModes.Edit, fieldToFocus: 'markup_type' },
                }));
                setHasUnsavedChanges(true);
              }}
            >
              Add markup
            </Button>
          </Stack>

          <Stack spacing={1.5}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography variant="h6">Conditions</Typography>
            </Stack>
            <DataGrid
              autoHeight
              hideFooter
              disableRowSelectionOnClick
              getRowHeight={() => 'auto'}
              rows={conditions}
              columns={conditionGridWithActions}
              columnVisibilityModel={{ debug: false }}
              editMode="row"
              rowModesModel={conditionRowModesModel}
              onRowModesModelChange={setConditionRowModesModel}
              onRowEditStop={handleRowEditStop}
              processRowUpdate={(updatedRow) => {
                setConditions((current) =>
                  current.map((row) => (row.id === updatedRow.id ? { ...row, ...updatedRow } : row)),
                );
                setHasUnsavedChanges(true);
                markConditionSaved(updatedRow.id);
                return updatedRow;
              }}
              onProcessRowUpdateError={(error) => {
                enqueueSnackbar(`Failed to update condition row: ${error}`, { variant: 'error' });
              }}
              sx={{
                '& .MuiDataGrid-cell': { alignItems: 'flex-start', py: 1 },
                '& .MuiDataGrid-row': { maxHeight: 'none !important' },
              }}
            />
            <Button
              startIcon={<AddIcon />}
              variant="text"
              onClick={() => {
                const newCondition = createDefaultCondition();
                if (!newCondition) {
                  return;
                }

                setConditions((current) => current.concat(newCondition));
                setConditionRowModesModel((current) => ({
                  ...current,
                  [newCondition.id]: { mode: GridRowModes.Edit, fieldToFocus: 'condition_type' },
                }));
                setHasUnsavedChanges(true);
              }}
            >
              Add condition
            </Button>
          </Stack>
        </Stack>

        <TextField
          label="Expires At"
          type="datetime-local"
          InputLabelProps={{ shrink: true }}
          value={expiresAt}
          onChange={(event) => {
            setExpiresAt(event.target.value);
            setHasUnsavedChanges(true);
          }}
          helperText="Optional expiry for the channel markup."
        />

        <DialogActions sx={{ px: 0, pt: 3 }}>
          <Button startIcon={<InfoOutlinedIcon />} variant="text" onClick={() => setShowDetails((current) => !current)}>
            {showDetails ? 'Hide details' : 'More details'}
          </Button>
          <Button onClick={() => goBack()}>Cancel</Button>
          {hasUnsavedChanges && (
            <Typography variant="body2" color="warning.main" sx={{ alignSelf: 'center', fontWeight: 600 }}>
              You have unsaved changes
            </Typography>
          )}
          {canEditMarkup && (
            <Box data-testid="channel-markup-v2-submit-button">
              <Button
                variant="contained"
                type="submit"
                disabled={isSubmitting || !canEditRows || isChecking || !canSave}
              >
                {isChecking ? 'Checking...' : isEditMode ? 'Update markup' : 'Create markup'}
              </Button>
            </Box>
          )}
        </DialogActions>

        <Alert severity="warning" variant="outlined">
          This is in active development - see channel markup v1 for current tested functionality
        </Alert>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <DebugModal type="generic" title="Channel Markup Debug" data={debugState} />
        </Box>
      </Box>

      <Box mt={2}>
        <Accordion defaultExpanded={false}>
          <AccordionSummary aria-controls="channel-markup-tester-content" id="channel-markup-tester-header">
            <Box>
              <Typography variant="h6">Channel markup tester</Typography>
              <Typography variant="body2" color="text.secondary">
                Test the current markup rules against a simulated offer and user context.
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={2}>
              <Stack spacing={2}>
                <Box
                  sx={{
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 2,
                    p: 2,
                    bgcolor: 'background.paper',
                  }}
                >
                  <Stack spacing={2}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      Offer info
                    </Typography>
                    <Stack spacing={2} direction={{ xs: 'column', md: 'row' }}>
                      <TextField fullWidth label="Offer ID" {...registerTester('context.offer.offerId')} />
                      <TextField fullWidth label="Offer type" {...registerTester('context.offer.loyaltyOfferType')} />
                    </Stack>
                    <Stack spacing={2} direction={{ xs: 'column', md: 'row' }}>
                      <TextField
                        fullWidth
                        label="Offer price"
                        type="number"
                        inputProps={{ step: '0.01' }}
                        {...registerTester('context.offer.total')}
                      />
                      <TextField
                        fullWidth
                        label="Lux Plus offer price"
                        type="number"
                        inputProps={{ step: '0.01' }}
                        {...registerTester('context.offer.luxPlusPrice')}
                      />
                    </Stack>
                  </Stack>
                </Box>

                <Box
                  sx={{
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 2,
                    p: 2,
                    bgcolor: 'background.paper',
                  }}
                >
                  <Stack spacing={2}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      Context info
                    </Typography>
                    <Stack spacing={2} direction={{ xs: 'column', md: 'row' }}>
                      <TextField fullWidth label="Region" {...registerTester('context.region')} />
                      <TextField fullWidth select label="Site location" {...registerTester('context.siteLocation')}>
                        {siteLocationConfig.map((option) => (
                          <MenuItem key={option.key} value={option.key}>
                            {option.displayName}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Stack>
                  </Stack>
                </Box>

                <Box
                  sx={{
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 2,
                    p: 2,
                    bgcolor: 'background.paper',
                  }}
                >
                  <Stack spacing={2}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      Audience info
                    </Typography>
                    <Stack spacing={2} direction={{ xs: 'column', md: 'row' }}>
                      <TextField fullWidth select label="Subscriber tier" {...registerTester('context.subscriberTier')}>
                        <MenuItem value="">None</MenuItem>
                        {subscriberTierOptions.map((option) => (
                          <MenuItem key={option.key} value={option.key}>
                            {option.displayName}
                          </MenuItem>
                        ))}
                      </TextField>
                      <TextField
                        fullWidth
                        select
                        label="User role"
                        value={testerValues.context.roles[0] ?? ''}
                        onChange={(event) =>
                          setTesterValue('context.roles', event.target.value ? [event.target.value] : [])
                        }
                      >
                        <MenuItem value="">None</MenuItem>
                        {userRoleOptions.map((option) => (
                          <MenuItem key={option.key} value={option.key}>
                            {option.displayName}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Stack>
                  </Stack>
                </Box>

                <Box
                  sx={{
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 2,
                    p: 2,
                    bgcolor: 'background.paper',
                  }}
                >
                  <Stack spacing={2}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      Currency info
                    </Typography>
                    <Stack spacing={1}>
                      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                        <Typography variant="body2" color="text.secondary">
                          Select one or more currencies to evaluate.
                        </Typography>
                      </Stack>
                      <TextField fullWidth select label="Currency" {...registerTester('context.currency')}>
                        {currencyOptions.map((option) => (
                          <MenuItem key={option.key} value={option.key}>
                            {option.displayName}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Stack>
                  </Stack>
                </Box>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <Button variant="contained" onClick={() => setTesterCheckVersion((current) => current + 1)}>
                  Check
                </Button>
                <Typography variant="body2" color="text.secondary">
                  Evaluates {testerResults.length} scenario{testerResults.length === 1 ? '' : 's'}.
                </Typography>
              </Stack>
              <Alert severity={testerResult === 'matched' ? 'success' : 'info'} variant="outlined">
                {testerResult === 'matched'
                  ? 'Markup matched at least one scenario.'
                  : testerResult === 'pending'
                    ? 'Evaluation is pending for at least one scenario.'
                    : 'No markup matched any scenario.'}
              </Alert>
              {testerResults.map((result) => (
                <Alert
                  key={result.currency}
                  severity={
                    result.evaluation.status === 'matched'
                      ? 'success'
                      : result.evaluation.status === 'pending'
                        ? 'info'
                        : 'warning'
                  }
                  variant="outlined"
                >
                  <Stack spacing={0.5}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2}>
                      <Typography variant="subtitle2">
                        {testerValues.context.offer.offerId || 'test-offer'} · {result.currency}
                      </Typography>
                      <DebugModal
                        type="generic"
                        title={`Evaluator result ${result.currency}`}
                        data={result}
                        hideIcon
                        buttonProps={{ size: 'small', variant: 'text' }}
                      >
                        Debug result
                      </DebugModal>
                    </Stack>
                    <Typography variant="body2">Evaluation status: {result.evaluation.status}</Typography>
                    <Typography variant="body2">Match count: {result.presentation?.matchCount ?? 0}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Effective inputs: {effectiveTesterContext.offer?.offerId} /{' '}
                      {effectiveTesterContext.offer?.loyaltyOfferType} / {effectiveTesterContext.region} /{' '}
                      {effectiveTesterContext.siteLocation} / {effectiveTesterContext.currency}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Audience: tier {effectiveTesterContext.subscriberTier ?? 'None'}, role{' '}
                      {effectiveTesterContext.roles?.[0] ?? 'None'}
                    </Typography>
                    {result.presentation ? (
                      <Typography variant="body2">
                        CTA: {result.presentation.channelMarkupCallToActionText || 'n/a'}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No markup matched this scenario.
                      </Typography>
                    )}
                    <Box sx={{ pt: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Evaluations
                      </Typography>
                      <Stack spacing={0.75} sx={{ mt: 0.5 }}>
                        {result.conditionEvaluations.length > 0 ? (
                          result.conditionEvaluations.map((evaluation) => (
                            <Box
                              key={evaluation.id}
                              sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 0.25,
                              }}
                            >
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {evaluation.conditionType}
                              </Typography>
                              <Typography
                                variant="body2"
                                color={evaluation.passed ? 'success.main' : 'error.main'}
                                sx={{ fontWeight: 600 }}
                              >
                                {evaluation.passed ? 'Matched' : 'Did not match'}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                Condition: {evaluation.conditionValue || 'n/a'}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                Context: {evaluation.contextValue || 'n/a'}
                              </Typography>
                            </Box>
                          ))
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No condition evaluations returned.
                          </Typography>
                        )}
                      </Stack>
                    </Box>
                  </Stack>
                </Alert>
              ))}
            </Stack>
          </AccordionDetails>
        </Accordion>
      </Box>
    </Container>
  );
}
